import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { Select } from 'src/components/molecules/select';
import { useUnblockSpace } from 'src/features/spaces/api/hooks/use-unblock-space';
import { SpaceUnavailabilityState } from 'src/features/spaces/api/types/response/space.response';
import { formatSpaceBlockingDateRange } from 'src/features/spaces/utils/format-space-blocking-date-range';
import { getDateIsoStringWithoutSecondsAndMilliseconds } from 'src/utils/get-date-iso-string-without-seconds-and-milliseconds';
import { z } from 'zod';
import { useUnblockSpaceDialogFormValidationSchema } from './schema/use-form-validation-schema';

interface Props extends DialogActions {
  spaceToken: string;
  spaceBlockedStates: SpaceUnavailabilityState[];
}

export const UnblockSpaceDialog = ({
  open,
  onCancel,
  spaceToken,
  spaceBlockedStates,
}: Props) => {
  const { t } = useTranslation(['spaces', 'common']);

  const formValidationSchema = useUnblockSpaceDialogFormValidationSchema();

  type FormValues = z.infer<typeof formValidationSchema>;

  const defaultValues: FormValues = {
    dateBlock: {
      from: null,
      until: null,
    },
  };

  const { control, reset, handleSubmit, setValue } = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(formValidationSchema),
    mode: 'onBlur',
  });

  const handleCancel = () => {
    onCancel?.();
    reset(defaultValues);
  };

  const { mutate: unblockSpace, isPending: isUnblockingSpace } =
    useUnblockSpace(handleCancel);

  const handleUnblockSpace = (data: FormValues) => {
    if (!data.dateBlock.from || !data.dateBlock.until) {
      return;
    }

    unblockSpace({
      spaceToken,
      payload: {
        from: getDateIsoStringWithoutSecondsAndMilliseconds(
          data.dateBlock.from,
        ),
        until: getDateIsoStringWithoutSecondsAndMilliseconds(
          data.dateBlock.until,
        ),
      },
    });
  };

  const selectDateBlockOptions = spaceBlockedStates.map(state => ({
    label: formatSpaceBlockingDateRange(state.from, state.until),
    value: JSON.stringify({
      from: state.from,
      until: state.until,
    }),
  }));

  return (
    <Dialog
      title={t('spaces:common.unblockSpaceDialog.title')}
      open={open}
      onCancel={handleCancel}
      onConfirm={handleSubmit(handleUnblockSpace)}
      isConfirmButtonSubmitting={isUnblockingSpace}>
      <Controller
        control={control}
        name="dateBlock"
        render={({
          field: { name, value, onBlur },
          fieldState: { error, invalid },
        }) => (
          <Select
            name={name}
            value={value}
            onChange={event => {
              const parsedValue = JSON.parse(event.target.value as string) as {
                from: number;
                until: number;
              };

              setValue('dateBlock', {
                from: new Date(parsedValue.from),
                until: new Date(parsedValue.until),
              });
            }}
            onBlur={onBlur}
            error={invalid}
            helperText={error?.message}
            label={t('spaces:common.unblockSpaceDialog.selectLabel')}
            items={selectDateBlockOptions}
            renderValue={value => {
              const typedValue = value as {
                from: Date;
                until: Date;
              };

              if (!typedValue.from || !typedValue.until) {
                return '';
              }

              return formatSpaceBlockingDateRange(
                typedValue.from.getTime(),
                typedValue.until.getTime(),
              );
            }}
          />
        )}
      />
    </Dialog>
  );
};
